/**
 * useFirestoreMutation — thin abstraction over Firestore write primitives.
 *
 * Exposes `fsDoc`, `fsCollection`, `fsSetDoc`, and `fsAddDoc` so that mutation
 * hooks can import from here instead of importing directly from
 * `firebase/firestore`. This is the Capacitor migration boundary: when
 * wrapping with Capacitor, only this module changes — callers stay the same.
 *
 * Capacitor swap point: replace with `@capacitor-firebase/firestore` equivalents
 * (FirebaseFirestore.setDocument / addDocument etc.) and update the types.
 *
 * All four functions are async — they await the lazily-loaded Firestore SDK
 * (see @/lib/firestoreSdk) before touching it, so `firebase/firestore` stays
 * out of the initial bundle. Every caller already lives inside an async
 * create/update callback, so this is a non-breaking change to call sites
 * beyond adding `await`.
 *
 * Offline semantics are unchanged: Firestore's persistentLocalCache queues
 * writes in IndexedDB and resolves immediately against the local cache.
 * Background sync happens on reconnect. The lazy SDK load only defers *when*
 * the persistent cache is established (first use, not app boot) — it is
 * always established before the first `setDoc`/`addDoc` call.
 */

import { getFirestoreSdk } from '@/lib/firestoreSdk';
import type {
  CollectionReference,
  DocumentData,
  DocumentReference,
  SetOptions,
} from 'firebase/firestore';

/**
 * Get a CollectionReference for the given path.
 * Mirrors `collection(db, path)`.
 */
export async function fsCollection(path: string): Promise<CollectionReference<DocumentData>> {
  const { sdk, db } = await getFirestoreSdk();
  return sdk.collection(db, path);
}

/**
 * Get a DocumentReference. When called with a CollectionReference and no id,
 * Firestore auto-generates an id (same as `doc(collectionRef)`).
 * When called with a path string and id segments, mirrors `doc(db, path, ...segments)`.
 */
export async function fsDoc(
  pathOrRef: string | CollectionReference,
  ...segments: string[]
): Promise<DocumentReference<DocumentData>> {
  const { sdk, db } = await getFirestoreSdk();
  if (typeof pathOrRef === 'string') {
    return sdk.doc(db, pathOrRef, ...segments);
  }
  // CollectionReference overload — auto-id doc.
  return sdk.doc(pathOrRef);
}

/**
 * Write (or merge into) a document at the given reference.
 * Pass `{ merge: true }` to preserve fields not present in `data`.
 * Mirrors `setDoc(ref, data, options?)`.
 */
export async function fsSetDoc(
  ref: DocumentReference<DocumentData>,
  data: DocumentData,
  options?: SetOptions,
): Promise<void> {
  const { sdk } = await getFirestoreSdk();
  return options ? sdk.setDoc(ref, data, options) : sdk.setDoc(ref, data);
}

/**
 * Add a document to a collection with an auto-generated id.
 * Mirrors `addDoc(collectionRef, data)`.
 */
export async function fsAddDoc(
  collectionRef: CollectionReference<DocumentData>,
  data: DocumentData,
): Promise<DocumentReference<DocumentData>> {
  const { sdk } = await getFirestoreSdk();
  return sdk.addDoc(collectionRef, data);
}
